import { type FC } from "react";
import { Button, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Filter, FilterProvider } from "@/partner/shared/components/Filter";
import {
  BROADCAST_FILTER_STATUSES,
  BROADCAST_STATUS_KEYS,
} from "../model/broadcastLabels";
import { BroadcastsList } from "./BroadcastsList";
import { CreateBroadcastDialog } from "./CreateBroadcastDialog";
import { BroadcastDialog } from "./BroadcastDialog";

/**
 * Рассылки (спека marketing-automations, фаза 2), устроены как библиотека
 * писем (EmailTemplatesLibrary): поиск и статус внутри панели фильтра,
 * создание коротким диалогом по ?tab=create, рассылка fullScreen-диалогом
 * по ?tab=edit&id=.
 */
const BroadcastsInner: FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  const editId = searchParams.get("id");

  const openCreate = () => {
    searchParams.set("tab", "create");
    setSearchParams(searchParams);
  };

  const openEdit = (id: string) => {
    searchParams.set("tab", "edit");
    searchParams.set("id", id);
    setSearchParams(searchParams);
  };

  // Созданная рассылка сразу открывается в редакторе, адрес меняется без
  // новой записи в истории: «Назад» не должен снова открывать выбор шаблона.
  const onCreated = (id: string) => {
    searchParams.set("tab", "edit");
    searchParams.set("id", id);
    setSearchParams(searchParams, { replace: true });
  };

  const closeDialog = () => {
    searchParams.delete("tab");
    searchParams.delete("id");
    setSearchParams(searchParams);
  };

  const statusOptions = BROADCAST_FILTER_STATUSES.map((status) => ({
    value: status,
    label: t(BROADCAST_STATUS_KEYS[status]),
  }));

  return (
    <>
      <Stack spacing={1}>
        <Filter
          title={t("broadcasts.list.title")}
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disableElevation
              onClick={openCreate}
            >
              {t("common.actions.add")}
            </Button>
          }
          fields={{ search: true }}
          selects={[
            {
              name: "status",
              label: t("broadcasts.filter.status"),
              options: statusOptions,
            },
          ]}
        />
        <BroadcastsList onEdit={openEdit} />
      </Stack>
      {tab === "create" && (
        <CreateBroadcastDialog onClose={closeDialog} onCreated={onCreated} />
      )}
      {tab === "edit" && editId && (
        <BroadcastDialog key={editId} id={editId} onClose={closeDialog} />
      )}
    </>
  );
};

export const Broadcasts: FC = () => (
  <FilterProvider>
    <BroadcastsInner />
  </FilterProvider>
);
